import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';

interface StaleItem {
  entity_type: 'order' | 'appointment' | 'refund';
  entity_id: number | string;
  current_state: string;
  state_since: string | null;
  sla_breach_minutes: number;
  severity: 'high' | 'medium' | 'low';
  label: string;
  meta?: Record<string, unknown> | null;
}

interface StaleResponse {
  items: StaleItem[];
  counts: { high: number; medium: number; low: number; total: number };
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function SeverityChip({ s }: { s: 'high' | 'medium' | 'low' }) {
  const map = {
    high: 'bg-red-100 text-red-700',
    medium: 'bg-amber-100 text-amber-700',
    low: 'bg-slate-100 text-slate-600',
  } as const;
  return (
    <span
      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${map[s]}`}
    >
      {s}
    </span>
  );
}

function linkFor(item: StaleItem): string | null {
  if (item.entity_type === 'order') return `/orders/${item.entity_id}`;
  if (item.entity_type === 'appointment')
    return `/consultations/${item.entity_id}`;
  if (item.entity_type === 'refund') return `/refunds`;
  return null;
}

export function ActionNeededPage() {
  const [data, setData] = useState<StaleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<StaleResponse>('/back-office/action-needed');
      setData(res.data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load action queue'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const groups = useMemo(() => {
    const items = data?.items ?? [];
    return {
      high: items.filter((i) => i.severity === 'high'),
      medium: items.filter((i) => i.severity === 'medium'),
      low: items.filter((i) => i.severity === 'low'),
    };
  }, [data]);

  return (
    <div className="px-8 py-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-800">Action needed</h1>
        <p className="text-sm text-slate-500">
          Orders, consultations, and refunds that have stalled past their SLA
          and need an operator to step in. Sorted by severity and age.
        </p>
      </div>

      {data && (
        <div className="mt-4 flex gap-4 text-sm">
          <span className="rounded bg-red-50 px-3 py-1 text-red-700">
            High: <strong>{data.counts.high}</strong>
          </span>
          <span className="rounded bg-amber-50 px-3 py-1 text-amber-700">
            Medium: <strong>{data.counts.medium}</strong>
          </span>
          <span className="rounded bg-slate-100 px-3 py-1 text-slate-600">
            Low: <strong>{data.counts.low}</strong>
          </span>
          <button
            type="button"
            onClick={load}
            className="ml-auto text-sm text-brand-600 hover:underline"
          >
            Refresh
          </button>
        </div>
      )}

      {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
      {!loading && error && (
        <div className="mt-6 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!loading && !error && data && data.counts.total === 0 && (
        <div className="mt-6 rounded border border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
          Clean queue — nothing past SLA.
        </div>
      )}

      {(['high', 'medium', 'low'] as const).map((sev) => {
        const list = groups[sev];
        if (!list.length) return null;
        return (
          <section key={sev} className="mt-6">
            <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
              {sev} severity ({list.length})
            </h2>
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-700">
                  <tr>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2">Item</th>
                    <th className="px-3 py-2">State</th>
                    <th className="px-3 py-2">Since</th>
                    <th className="px-3 py-2">SLA breach</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((i, idx) => {
                    const href = linkFor(i);
                    const breachHrs = Math.round(i.sla_breach_minutes / 60);
                    return (
                      <tr
                        key={`${i.entity_type}:${i.entity_id}:${idx}`}
                        className="border-t border-slate-100 align-top"
                      >
                        <td className="px-3 py-2 text-xs uppercase text-slate-500">
                          {i.entity_type}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="font-medium text-slate-800">
                            {i.label}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <SeverityChip s={i.severity} />
                          <span className="ml-2 text-slate-500">
                            {i.current_state}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-500">
                          {formatDate(i.state_since)}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-600">
                          {breachHrs >= 1
                            ? `${breachHrs}h`
                            : `${i.sla_breach_minutes}m`}{' '}
                          over
                        </td>
                        <td className="px-3 py-2 text-right">
                          {href && (
                            <Link
                              to={href}
                              className="text-xs font-medium text-brand-600 hover:underline"
                            >
                              Open →
                            </Link>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        );
      })}
    </div>
  );
}

export default ActionNeededPage;
